import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/components/AuthProvider';
import { useCarbonProjects } from '@/hooks/useCarbonProjects';
import { useToast } from '@/hooks/use-toast';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Upload,
  FileText,
  Send,
  Eye,
  Download,
  Calendar,
  Building2,
  Shield
} from 'lucide-react';

const ProjectVerification = () => {
  const { user } = useAuth();
  const { projects, loading } = useCarbonProjects(user?.id || '');
  const { toast } = useToast();

  const mockVerificationBodies = [
    {
      id: 1,
      name: "Green Verify International",
      accreditation: "CDM Gold Standard",
      rating: 4.8,
      completedProjects: 150,
      avgTimeline: "45 days",
      specialization: ["Solar", "Wind", "Biogas"]
    },
    {
      id: 2,
      name: "Carbon Trust Solutions",
      accreditation: "VCS, CAR",
      rating: 4.6,
      completedProjects: 230,
      avgTimeline: "38 days",
      specialization: ["Forestry", "REDD+", "Cookstoves"]
    },
    {
      id: 3,
      name: "EcoVeritas India",
      accreditation: "Gold Standard, VCS",
      rating: 4.9,
      completedProjects: 95,
      avgTimeline: "52 days",
      specialization: ["Renewable Energy", "Waste Management"]
    }
  ];

  const mockVerificationProgress = [
    {
      projectId: "1",
      projectName: "Solar Farm Maharashtra",
      verificationBody: "Green Verify International",
      status: "in_progress",
      progress: 65,
      steps: [
        { name: "Initial Review", status: "completed", date: "2024-12-01" },
        { name: "Site Visit", status: "completed", date: "2024-12-10" },
        { name: "Technical Assessment", status: "in_progress", date: "2024-12-20" },
        { name: "Final Report", status: "pending", date: null },
        { name: "Certification", status: "pending", date: null }
      ]
    },
    {
      projectId: "2",
      projectName: "Wind Power Tamil Nadu",
      verificationBody: "Carbon Trust Solutions",
      status: "completed",
      progress: 100,
      steps: [
        { name: "Initial Review", status: "completed", date: "2024-11-15" },
        { name: "Site Visit", status: "completed", date: "2024-11-25" },
        { name: "Technical Assessment", status: "completed", date: "2024-12-05" },
        { name: "Final Report", status: "completed", date: "2024-12-15" },
        { name: "Certification", status: "completed", date: "2024-12-18" }
      ]
    }
  ];

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'completed': { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Verified' },
      'in_progress': { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'In Progress' },
      'pending': { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'rejected': { icon: AlertCircle, color: 'bg-red-100 text-red-800', label: 'Needs Review' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    const IconComponent = config.icon;
    
    return (
      <Badge className={config.color}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-orange-600" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const handleUploadDocument = (projectId: string) => {
    toast({
      title: "Document Upload",
      description: "Document upload functionality will be implemented soon",
    });
  };

  const handleSelectVerificationBody = (bodyId: number) => {
    toast({
      title: "Verification Body Selected",
      description: "Verification process initiated successfully",
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Project Verification</h1>
          <p className="text-muted-foreground">
            Manage verification processes and track progress with accredited verification bodies
          </p>
        </div>

        <Tabs defaultValue="tracking" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="tracking">Verification Tracking</TabsTrigger>
            <TabsTrigger value="bodies">Verification Bodies</TabsTrigger>
            <TabsTrigger value="progress">Progress Monitoring</TabsTrigger>
            <TabsTrigger value="documents">Document Management</TabsTrigger>
          </TabsList>

          <TabsContent value="tracking" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Project Verification Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {mockVerificationProgress.map((item) => (
                    <Card key={item.projectId} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{item.projectName}</h3>
                            <p className="text-sm text-muted-foreground">
                              Verification Body: {item.verificationBody}
                            </p>
                          </div>
                          {getStatusBadge(item.status)}
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">Overall Progress</span>
                            <span className="text-sm text-muted-foreground">{item.progress}%</span>
                          </div>
                          <Progress value={item.progress} className="h-2" />
                        </div>

                        <div className="space-y-3">
                          {item.steps.map((step, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                              {getStepIcon(step.status)}
                              <div className="flex-1">
                                <span className="font-medium">{step.name}</span>
                                {step.date && (
                                  <span className="ml-3 text-sm text-muted-foreground">
                                    {step.date}
                                  </span>
                                )}
                              </div>
                              {step.status === 'completed' && (
                                <Button variant="ghost" size="sm">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bodies" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Accredited Verification Bodies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  {mockVerificationBodies.map((body) => (
                    <Card key={body.id} className="border hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{body.name}</h3>
                              <div className="flex items-center gap-1">
                                <div className="flex text-yellow-400">
                                  {[...Array(5)].map((_, i) => (
                                    <span key={i}>
                                      {i < Math.floor(body.rating) ? '★' : '☆'}
                                    </span>
                                  ))}
                                </div>
                                <span className="text-sm text-muted-foreground ml-1">
                                  {body.rating}
                                </span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-muted-foreground">Accreditation:</span>
                                <div className="font-medium">{body.accreditation}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Completed Projects:</span>
                                <div className="font-medium">{body.completedProjects}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Avg Timeline:</span>
                                <div className="font-medium">{body.avgTimeline}</div>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {body.specialization.map((spec, index) => (
                                <Badge key={index} variant="outline">{spec}</Badge>
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button 
                              onClick={() => handleSelectVerificationBody(body.id)}
                            >
                              Select
                            </Button>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              View Profile
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="progress" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Progress Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {mockVerificationProgress.map((item) => (
                    <Card key={item.projectId} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-lg font-semibold">{item.projectName}</h3>
                          {getStatusBadge(item.status)}
                        </div>
                        
                        <div className="relative">
                          <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"></div>
                          <div className="space-y-6">
                            {item.steps.map((step, index) => (
                              <div key={index} className="flex items-center gap-4 relative">
                                <div className="relative z-10">
                                  {getStepIcon(step.status)}
                                </div>
                                <div className="flex-1 flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                                  <div>
                                    <h4 className="font-medium">{step.name}</h4>
                                    {step.date && (
                                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {step.date}
                                      </p>
                                    )}
                                  </div>
                                  {step.status === 'completed' && (
                                    <div className="flex gap-2">
                                      <Button variant="ghost" size="sm">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button variant="ghost" size="sm">
                                        <Download className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Document Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {projects.slice(0, 3).map((project) => (
                    <Card key={project.registry_id} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{project.name}</h3>
                            <p className="text-sm text-muted-foreground">{project.location}</p>
                          </div>
                          {getStatusBadge(project.status)}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-primary cursor-pointer text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm font-medium">Project Design Document</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => handleUploadDocument(project.registry_id)}
                            >
                              Upload
                            </Button>
                          </div>
                          
                          <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-primary cursor-pointer text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm font-medium">Monitoring Report</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => handleUploadDocument(project.registry_id)}
                            >
                              Upload
                            </Button>
                          </div>
                          
                          <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-primary cursor-pointer text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm font-medium">Environmental Impact</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => handleUploadDocument(project.registry_id)}
                            >
                              Upload
                            </Button>
                          </div>
                          
                          <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-primary cursor-pointer text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm font-medium">Financial Records</p>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => handleUploadDocument(project.registry_id)}
                            >
                              Upload
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-4">
                          <Button variant="outline" size="sm">
                            <Send className="w-4 h-4 mr-2" />
                            Submit to Verification Body
                          </Button>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            View All Documents
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ProjectVerification;